import React, { useEffect, useRef, useState } from "react";
import { Check, Clock } from "lucide-react";
import { useBooking } from "@/context/BookingContext";
import { PrimaryButton } from "@/components/BookingShell";
import { GoldRule } from "@/components/Brand";
import { api } from "@/lib/api";
import { formatFullDate, formatPrice, computeDeposit } from "@/lib/format";

/**
 * Success screen — reflects the true Amelia state:
 *
 *   status === "approved"  → "Appointment confirmed"
 *   status === "pending"   → "Appointment reserved — deposit required"
 *
 * The initial status comes from the booking POST response. We then poll
 * GET /api/booking/appointments/{id}/status every 8s so the screen flips
 * to "confirmed" the moment Amelia records the customer's Stripe deposit
 * (via Amelia's webhook -> booking approved). No API keys or Stripe SDKs
 * are ever loaded in the browser.
 */
export function SuccessStep() {
    const { service, date, time, customer, confirmation, reset } = useBooking();
    const initialStatus = (confirmation?.status || "pending").toLowerCase();
    const [status, setStatus] = useState(initialStatus);
    const stopRef = useRef(false);

    const isApproved = status === "approved";
    const { depositDue, hasDeposit } = computeDeposit(service);

    useEffect(() => {
        // Only poll while pending, and only if we know the appointment id.
        const id = confirmation?.appointment_id;
        if (!id || status === "approved") return undefined;
        stopRef.current = false;
        let attempts = 0;
        const tick = async () => {
            if (stopRef.current) return;
            attempts += 1;
            try {
                const res = await api.getAppointmentStatus(id);
                const s = (res?.status || "").toLowerCase();
                if (s && s !== status) setStatus(s);
                if (s === "approved") return;
            } catch (_) {
                /* transient — keep polling */
            }
            // Poll every 8s for the first 5 minutes, then back off to 30s.
            const wait = attempts < 38 ? 8000 : 30000;
            setTimeout(tick, wait);
        };
        const t = setTimeout(tick, 8000);
        return () => { stopRef.current = true; clearTimeout(t); };
    }, [confirmation, status]);

    return (
        <div className="pt-8 text-center animate-fade-up" data-testid="success-step">
            <div
                className={`mx-auto h-20 w-20 rounded-full glass flex items-center justify-center border ${
                    isApproved ? "border-gold/60" : "border-gold/30"
                }`}
                data-testid="success-status-icon"
            >
                {isApproved ? (
                    <Check className="h-9 w-9 text-gold" strokeWidth={1.5} />
                ) : (
                    <Clock className="h-8 w-8 text-gold" strokeWidth={1.25} />
                )}
            </div>

            <div className="mt-8">
                <div
                    className="text-[10px] uppercase tracking-[0.28em] text-gold/85"
                    data-testid="success-status-label"
                >
                    {isApproved ? "Confirmed" : "Deposit required"}
                </div>
                <h1
                    className="mt-2 font-serif font-light text-4xl sm:text-5xl leading-[1.05] text-white"
                    data-testid="success-headline"
                >
                    {isApproved ? (
                        <>Appointment <span className="gold-text">confirmed</span></>
                    ) : (
                        <>Appointment <span className="gold-text">reserved</span></>
                    )}
                </h1>
                <p
                    className="mt-4 text-white/60 text-sm max-w-sm mx-auto"
                    data-testid="success-subcopy"
                >
                    {isApproved ? (
                        <>
                            Thank you, {customer.firstName || "friend"}. Your appointment with Angela is
                            confirmed. A confirmation has been sent to{" "}
                            <span className="text-white">{customer.email}</span>.
                        </>
                    ) : hasDeposit ? (
                        <>
                            Your slot is held for you. Please check your inbox at{" "}
                            <span className="text-white">{customer.email}</span> for a secure Stripe
                            link to pay the {formatPrice(depositDue)} deposit and confirm your booking.
                        </>
                    ) : (
                        <>
                            Your slot is held for you. Please check your inbox at{" "}
                            <span className="text-white">{customer.email}</span> for your appointment
                            confirmation.
                        </>
                    )}
                </p>
            </div>

            <GoldRule className="mt-8" />

            <div className="mt-6 rounded-3xl glass p-6 text-left space-y-3" data-testid="success-summary">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">Treatment</div>
                    <div className="font-serif text-2xl text-white leading-snug">{service?.name}</div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">When</div>
                    <div className="text-white font-ui">{formatFullDate(date)} · {time}</div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">Practitioner</div>
                    <div className="text-white font-ui">Angela</div>
                </div>
                {hasDeposit && !isApproved && (
                    <div className="pt-2" data-testid="success-deposit-row">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-gold/85">Deposit due</div>
                        <div className="text-white font-ui text-base">
                            {formatPrice(depositDue)}{" "}
                            <span className="text-white/50">— check your email for the payment link</span>
                        </div>
                    </div>
                )}
                {confirmation?.appointment_id && (
                    <div className="pt-2">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">Reference</div>
                        <div className="text-white/70 font-ui text-sm" data-testid="success-reference">
                            #{confirmation.appointment_id}
                        </div>
                    </div>
                )}
            </div>

            {!isApproved && (
                <p className="mt-4 text-[11px] text-white/35" data-testid="success-waiting-hint">
                    This page will update automatically once we receive your deposit.
                </p>
            )}

            <div className="mt-10">
                <PrimaryButton onClick={reset} testId="success-new-booking-button">
                    Book another treatment
                </PrimaryButton>
            </div>
        </div>
    );
}
